import type { ECElementEvent } from 'echarts';
import ReactECharts, { EChartsOption } from 'echarts-for-react';
import type { JSX } from 'react';

import { useStableChartOption } from './StableChartOption';
import { ChartDataItem, ChartDimensions, chartFont } from './charts.common';

export interface HorizontalStackedBarChartProps {
    /** The title of the chart. */
    name: string;
    /** Each element corresponds with a bar. Earlier elements are rendered closer to the left of the chart. `color` should always be provided, even though it is typed as optional. */
    bars: ChartDataItem[];
    chartDimensions: ChartDimensions;
    onChartClick?: (item: string) => void;
    // TODO: Why is this necessary? (It's only used in one place, to pass in "bottom".)
    tooltipDirection?: 'bottom' | 'inside' | 'top' | 'left' | 'right';
}

/**
 * Renders a single stack of colored bars horizontally. Each bar has a width proportional to its value.
 */
export const HorizontalStackedBarChart = (props: HorizontalStackedBarChartProps): JSX.Element => {
    const primaryFont = getComputedStyle(document.documentElement).getPropertyValue(chartFont);

    const ariaDescription = (() => {
        const categoriesAndValues = props.bars.map((element) => `${element.value} ${element.name}`).join(', ');
        return `A segmented progress bar chart titled "${props.name}". There are ${categoriesAndValues}.`;
    })();

    const getOption = (info: Omit<HorizontalStackedBarChartProps, 'onChartClick'>, primaryFont: string): EChartsOption => ({
        tooltip: {
            trigger: 'item',
            axisPointer: { show: false },
            formatter: (params: { marker: string; seriesName: string; data: number }) => {
                return `${info.name} <br/> ${params.marker}${params.seriesName} &emsp; <b>${params.data}</b>`;
            },
            position: props.tooltipDirection,
            confine: props.tooltipDirection !== undefined,
        },
        textStyle: {
            fontFamily: primaryFont,
        },
        grid: {
            left: 0,
            top: 0,
            right: 0,
            bottom: 0,
        },
        legend: { show: false },
        xAxis: {
            type: 'value',
            show: false,
            axisPointer: {
                show: false,
            },
            max: 'dataMax',
        },
        yAxis: {
            type: 'category',
            data: [info.name],
            show: false,
            min: 'dataMin',
            max: 'dataMax',
            axisPointer: {
                show: false,
            },
        },
        series: createSeriesData(info.bars),
    });

    const createSeriesData = (data: ChartDataItem[]) => {
        let indexOfLeftmostBarWithNonZeroValue = data.findIndex((item) => item.value !== 0);
        if (indexOfLeftmostBarWithNonZeroValue === -1) {
            indexOfLeftmostBarWithNonZeroValue = 0;
        }

        let indexOfRightmostBarWithNonZeroValue = data.findLastIndex((item) => item.value !== 0);
        if (indexOfRightmostBarWithNonZeroValue === -1) {
            indexOfRightmostBarWithNonZeroValue = data.length - 1;
        }

        const getBorderRadii = (index: number): number[] => {
            let borderRadiusTopLeft = 0;
            let borderRadiusTopRight = 0;
            let borderRadiusBottomRight = 0;
            let borderRadiusBottomLeft = 0;

            if (index === indexOfLeftmostBarWithNonZeroValue) {
                borderRadiusTopLeft = 100;
                borderRadiusBottomLeft = 100;
            }

            if (index === indexOfRightmostBarWithNonZeroValue) {
                borderRadiusTopRight = 100;
                borderRadiusBottomRight = 100;
            }

            return [borderRadiusTopLeft, borderRadiusTopRight, borderRadiusBottomRight, borderRadiusBottomLeft];
        };

        return data.map((dataItem, index) => ({
            name: dataItem.name,
            type: 'bar',
            stack: 'value',
            itemStyle: { borderRadius: getBorderRadii(index), color: dataItem.color },
            emphasis: {
                focus: 'series',
            },
            data: [dataItem.value],
        }));
    };

    const option = useStableChartOption(getOption(props, primaryFont));

    return <ReactECharts aria-label={ariaDescription} role="img" option={option} style={{ height: props.chartDimensions.height, width: props.chartDimensions.width }} onEvents={{ click: (params: ECElementEvent) => props.onChartClick?.(params.seriesName!) }} />;
};
